import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { FiHeart, FiPlus } from "react-icons/fi";
import "../css/main.scss";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";

function Main() {
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [addedId, setAddedId] = useState(null);
  const [failedImages, setFailedImages] = useState([]);

  const hotPicks = productData.filter((product) => product.featured).slice(0, 6);
  const ritualProducts = [22, 12, 13, 23, 24, 20]
    .map((id) => productData.find((product) => product.id === id))
    .filter(Boolean);
  const stories = [18, 15, 14, 25]
    .map((id) => productData.find((product) => product.id === id))
    .filter(Boolean);

  const addProduct = (product) => {
    addToCart(product.id, 1, product.size[0], product.colors[0]);
    setAddedId(product.id);
    window.dispatchEvent(new CustomEvent("ottireluna:cart-open"));
    window.setTimeout(() => setAddedId(null), 1600);
  };

  const markImageFailed = (id) => {
    setFailedImages((current) => (current.includes(id) ? current : [...current, id]));
  };

  const productFeed = (products, label) => (
    <div className="home-feed" aria-label={label}>
      {products.map((product) => (
        <article className="home-product" key={product.id}>
          <div className={`home-product__media ${failedImages.includes(product.id) ? "is-error" : ""}`}>
            {product.badge && <span className="home-product__badge">{product.badge}</span>}
            <button
              className={`home-product__wish ${isWishlisted(product.id) ? "is-active" : ""}`}
              type="button"
              aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
              aria-pressed={isWishlisted(product.id)}
              onClick={() => toggleWishlist(product.id)}
            >
              <FiHeart aria-hidden="true" />
            </button>
            <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
              {failedImages.includes(product.id) ? (
                <span className="home-product__image-error">Image unavailable</span>
              ) : (
                <img
                  src={product.image}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                  onError={() => markImageFailed(product.id)}
                />
              )}
            </Link>
          </div>
          <div className="home-product__info">
            <Link to={`/shopDetails/${product.id}`}>
              <h3>{product.name}</h3>
            </Link>
            <span>{formatPrice(product.price)}</span>
          </div>
          <button className="home-product__add" type="button" onClick={() => addProduct(product)}>
            <span>{addedId === product.id ? "Added" : "Add to cart"}</span>
            <FiPlus aria-hidden="true" />
          </button>
        </article>
      ))}
    </div>
  );

  return (
    <main className="home-page" id="main-content">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <div className="home-hero__image">
          <img
            src="/main/ottireluna/hero-home.jpg"
            alt="Home accessories arranged in a bright Hong Kong apartment"
            loading="eager"
          />
        </div>
        <div className="home-hero__content">
          <p className="eyebrow">New everyday stories</p>
          <h1 id="home-hero-title">Objects made for the sunniest part of your day.</h1>
          <p>
            Useful, tactile pieces selected in Hong Kong for calm, colourful and
            practical contemporary living.
          </p>
          <Link className="button button--dark" to="/shop">
            Shop the collection
          </Link>
        </div>
      </section>

      <section className="sunshine-quote" aria-label="Brand statement">
        <span className="sunshine-quote__sun" aria-hidden="true" />
        <h2>{siteConfig.location}.<br />Small objects for warmer homes.</h2>
      </section>

      <section className="home-section home-section--pink" aria-labelledby="hot-picks-title">
        <div className="home-section__heading">
          <h2 id="hot-picks-title">This week’s hot picks</h2>
          <Link to="/shop">View all <span aria-hidden="true">→</span></Link>
        </div>
        {productFeed(hotPicks, "This week’s hot picks")}
      </section>

      <section className="home-editorial home-editorial--image-left" aria-labelledby="paper-vases-title">
        <div className="home-editorial__image">
          <img
            src="/main/ottireluna/dining-morning.jpg"
            alt="Ceramic vases in a bright contemporary interior"
            loading="lazy"
          />
        </div>
        <div className="home-editorial__content">
          <p className="eyebrow">Poetry in bloom</p>
          <h2 id="paper-vases-title">Discover our vases</h2>
          <p>Glass and ceramic silhouettes made for flowers and quiet corners.</p>
          <Link className="button" to="/shop?category=Vases">Shop vases</Link>
        </div>
      </section>

      <section className="home-section home-section--cream" aria-labelledby="ritual-title">
        <div className="home-section__heading">
          <h2 id="ritual-title">Made for your table ritual</h2>
          <Link to="/shop?category=Tableware">View tableware <span aria-hidden="true">→</span></Link>
        </div>
        {productFeed(ritualProducts, "Made for your table ritual")}
      </section>

      <section className="press-strip" aria-label="Selected press quotes">
        <blockquote>
          “Colour, craft and a little mischief — objects that make the everyday feel collected.”
          <cite>— The Design Edit</cite>
        </blockquote>
        <blockquote>
          “Useful forms with the confidence of contemporary design.”
          <cite>— Living Notes</cite>
        </blockquote>
        <blockquote>
          “A joyful approach to the things we keep close.”
          <cite>— Paper &amp; Home</cite>
        </blockquote>
      </section>

      <section className="home-stories" aria-labelledby="stories-title">
        <header>
          <p className="eyebrow">Objects with a point of view</p>
          <h2 id="stories-title">Four bright ideas</h2>
        </header>
        <div className="home-stories__grid">
          {stories.map((product, index) => (
            <article className="home-story" key={product.id}>
              <Link className="home-story__image" to={`/shopDetails/${product.id}`}>
                <img src={product.image} alt={product.name} loading="lazy" />
              </Link>
              <div className="home-story__content">
                <span>0{index + 1}</span>
                <h3>{product.name}</h3>
                <p>{product.description[0]}</p>
                <Link to={`/shopDetails/${product.id}`}>Shop now</Link>
              </div>
            </article>
          ))}
        </div>
      </section>

    </main>
  );
}

export default Main;
